import { __decorate } from "tslib";
import { message } from 'ant-design-vue';
import cloneDeep from "lodash-es/cloneDeep";
import { getDefaultValue } from '@tencent/data-schema-core';
import { State, Getter } from 'vue-class-state';
import { BaseState } from "./BaseState";
import debounce from "lodash-es/debounce";
import { createListItemState, isListItemState } from "./scope/ListItemState";
import { LazyGetter } from "../../tools/decorator/LazyGetter";
import { Action } from "./utils/Action";
import { arrayRemove } from "../../tools/lang";
import { getEntityEnumArray, getListProps, listActions, listWatcherSchema, transformPrimitiveValue } from "./ListState.helper";
import { hideProp } from "../../tools/hideProp";
import { Enumerable } from "../../tools/enumerable";
import { hasOwn } from "../../tools/hasOwn";
import { isPrimitive } from 'vue-class-component/lib/util';
import { isObject } from "lodash-es";
import isShowMessageSchema from "./param/isShowMessageSchema";
import { resSchema } from "./param/getResultDesc";
import { StateDefine } from "./utils/StateDefine";
import { bodyFilterField, bodySortField } from "../templates/apis/list";
import { defineArgs } from "./param/defineArgs";
import { getRealResponseSchema } from "../common/schemaUtil";
import tsCode from "./param/tsCode";
import { excludeSchema, getListDataSchema, includeSchema, pageSchema, sizeSchema, totalSchema } from "../stateCommon/list";
/**
 * 列表状态
 *
 * 1、考虑loading
 * 2、考虑error
 */
export class ListState extends BaseState {
  constructor(collection, options) {
    super(collection, options);
    this.stateModel = 'list';
    this.ucTag = '列表';
    /**
     * 列表数据
     */
    this.listItemsStates = [];
    this.selectedItems = [];
    this.page = 1;
    this.size = 20;
    this.include = '';
    this.exclude = '';
    this.filter = [];
    this.sort = [];
    this.total = 0;
    this.extra = {};
    this.initState();
  }
  get listData() {
    return this.listItemsStates;
  }
  set listData(val) {
    // 这样用户可以直接 data.xxx.listData = newVal 了,自动转化
    if (!Array.isArray(val)) {
      throw new Error('listData setter 调用失败，入参不是数组类型');
    }
    const transform = item => {
      if (isListItemState(item) || isPrimitive(item)) {
        return;
      }
      createListItemState(item, this);
    };
    val.forEach(transform);
    // 拦截会改写数组的api
    ['push', 'unshift', 'splice'].forEach(name => {
      if (hasOwn(val, name)) {
        return;
      }
      hideProp(val, name, {
        value: function listDataMutator(...args) {
          const items = name === 'splice' ? args.slice(2) : args;
          items.forEach(transform);
          const res = this.__proto__[name].apply(this, args); // vue是直接改__proto__原型的
          return res;
        },
        configurable: true
      });
    });
    this.listItemsStates = val;
  }
  /**
   * Array<IFilterItem> 类型的json字符串
   */
  get filterParams() {
    try {
      return JSON.stringify(this.pureFilter); // 请求的时候自动encode
    } catch (error) {
      console.error(error);
      return '';
    }
  }
  /**
   * 保证数据的key不为空
   */
  get pureFilter() {
    const filter = this.filter.filter(item => Boolean(item.k.trim()));
    const entity = this.getEntitySchema();
    if (!entity || !entity.fields) {
      return filter;
    }
    const {
      fields
    } = entity;
    const cloneFilter = cloneDeep(filter);
    cloneFilter.forEach(item => {
      // 只支持第一层 把类型转一下
      const target = fields === null || fields === void 0 ? void 0 : fields.find(i => i.id === item.k);
      if (!target || !isPrimitive(item.v)) {
        // 找不到schema或者不是值类型
        return;
      }
      transformPrimitiveValue(item, target);
    });
    return cloneFilter;
  }
  get pureSort() {
    return this.sort.filter(item => Boolean(item.k.trim()));
  }
  get querySchema() {
    const {
      list
    } = this.collection.collectionData.cgiInfo;
    return list.fixedQuerySchema || list.querySchema;
  }
  get supportedQuery() {
    const isSupported = id => {
      var _this$querySchema$fie;
      return ((_this$querySchema$fie = this.querySchema.fields) === null || _this$querySchema$fie === void 0 ? void 0 : _this$querySchema$fie.some(item => item.id === id)) || false;
    };
    return {
      /**
       * 是否支持page参数
       */
      page: isSupported('page'),
      /**
       * 是否支持size参数
       */
      size: isSupported('size'),
      /**
       * 是否支持过滤器
       * 如果用户有配置filter字段就认为支持过滤器
       */
      filter: isSupported('filter'),
      include: isSupported('include'),
      exclude: isSupported('exclude')
    };
  }
  get sortParams() {
    return this.pureSort.map(item => item.k).join(',');
  }
  get orderParams() {
    return this.pureSort.map(item => item.o).join(',');
  }
  async init() {
    const promises = [this.dispatch(listActions.list)];
    if (this.getActionSupported(listActions.count)) {
      promises.push(this.dispatch(listActions.count));
    }
    await Promise.all(promises);
  }
  async listAction(isShowMessage = true, param) {
    const modelRequest = () => {
      const baseOptions = {
        page: this.page,
        size: this.size,
        include: this.include,
        exclude: this.exclude
      };
      const res = this.collection.list({
        query: {
          ...baseOptions,
          filter: this.filterParams,
          sort: this.sortParams,
          order: this.orderParams
        },
        body: {
          ...baseOptions,
          filter: this.pureFilter,
          sort: this.pureSort
        }
      });
      return res;
    };
    // 用户参数直接透传，不走动参
    const paramRequest = () => this.collection.list(param, {
      raw: true
    });
    const res = await (typeof param === 'object' ? paramRequest() : modelRequest());
    return this.checkSucc(res.body, {
      cgiName: 'list',
      success: () => {
        const {
          data
        } = res.body;
        if (!(Array.isArray(data) || data === null)) {
          const {
            collectionId
          } = this.collection.collectionData;
          throw new Error(`${collectionId}数据源/[list]接口响应数据的data字段不是Array或null类型`);
        }
        if (Array.isArray(data)) {
          this.listData = res.body.data;
        }
        // 后台框架很多时候如果查询不到数据，会返回null，而无法返回[],技术限制,这里对null做下兼容
        if (data === null) {
          this.listData = [];
        }
        this.selectedItems = [];
        if (typeof res.body.total === 'number') {
          this.total = res.body.total;
        }
        if (hasOwn(res.body, 'extra')) {
          this.extra = res.body.extra;
        }
      },
      showError: isShowMessage
    });
  }
  async countAction(isShowMessage = true, param) {
    const res = await (isObject(param) ? this.collection.count(param, {
      raw: true
    }) : this.collection.count({
      query: {
        filter: this.filterParams
      },
      body: {
        filter: this.pureFilter
      }
    }));
    return this.checkSucc(res.body, {
      cgiName: 'count',
      success: () => {
        this.total = res.body.data.total;
      },
      showError: isShowMessage
    });
  }
  /**
   * 批量删除 已经选中的
   */
  async deleteSelected(isShowMessage = true) {
    if (this.selectedItems.length <= 0) {
      const msg = '未有选中行';
      message.warn(msg);
      return {
        success: false,
        message: msg
      };
    }
    const promises = this.selectedItems.map(item => item.$delete(isShowMessage));
    const results = await Promise.all(promises);
    return {
      success: results.every(item => item.success)
    };
  }
  /**
   * 批量保存
   */
  async saveSelected(isShowMessage = true) {
    if (this.selectedItems.length <= 0) {
      message.warn('未有选中行');
    }
    let isSuccess = true;
    const promises = this.selectedItems.map(async item => {
      const res = await item.$save(isShowMessage);
      if (res.success) {
        // 成功的自动去除选择态
        arrayRemove(this.selectedItems, item);
      } else {
        isSuccess = false;
      }
    });
    await Promise.all(promises);
    return {
      success: isSuccess
    };
  }
  async newItem(position = 'tail') {
    const schema = this.collection.getEntitySchema();
    const defaultData = schema ? getDefaultValue(schema) : {};
    const state = createListItemState(defaultData, this, {
      isFromCopy: true
    });
    if (position === 'tail') {
      this.listData.push(state);
    } else {
      this.listData.unshift(state);
    }
    return {
      data: state,
      success: true
    };
  }
  /**
   * 不分页，批量获取数据，根据filter过滤
   * 适用于db数据源，http数据源如果参数不规范，调用会有问题
   * @param extraParams 扩展参数，解决用户参数比模板多的情况
   * @returns
   */
  async batchList(extraParams = {
    query: {},
    body: {}
  }, maxSize = 10000) {
    let page = 0;
    let listData = [];
    let total = 0;
    let lastTotal = -1;
    const baseRequest = async () => {
      // 如果用户配置了动参，这里的逻辑很奇怪
      // 不走动参 其他参数怎么传 ？？ extraParams
      // 走动参page会被覆盖
      const baseOptions = {
        size: maxSize,
        include: this.include,
        exclude: this.exclude
      };
      const {
        body
      } = await this.collection.list({
        query: {
          ...baseOptions,
          filter: this.filterParams,
          sort: this.sortParams,
          order: this.orderParams,
          ...extraParams.query,
          page
        },
        body: {
          ...baseOptions,
          filter: this.pureFilter,
          sort: this.pureSort,
          ...extraParams.body,
          page
        }
      }, {
        raw: true
      });
      if (Array.isArray(body.data)) {
        listData = listData.concat(body.data);
        lastTotal = body.data.length;
        total += lastTotal;
      } else {
        throw new Error(' body.data is not a array');
      }
    };
    while (!(lastTotal === 0 || lastTotal > 0 && lastTotal < maxSize)) {
      //  = 0 没查到数据   <max 已经是最后一页
      page += 1;
      await baseRequest();
    }
    return {
      success: true,
      data: {
        listData,
        total,
        page,
        maxSize
      }
    };
  }
  /**
   * 创建一个 ListItemState , 用户应该暂时不会有这个需求
   * @param newListData
   */
  async $createListItemState(rawData, options = {
    isFromCopy: false
  }) {
    const data = createListItemState(rawData, this, options);
    return {
      success: true,
      data
    };
  }
  /**
   * 只从内存删除
   */
  removeItem(item) {
    arrayRemove(this.listData, item);
    arrayRemove(this.selectedItems, item);
  }
  registerWatcher() {
    const {
      stateWatcher
    } = this;
    const updateList = debounce(() => this.dispatch(listActions.list), 100);
    const events = [{
      id: 'page',
      trigger: () => this.page,
      callback: updateList
    }, {
      id: 'size',
      trigger: () => this.size,
      callback: updateList
    }];
    events.forEach(e => stateWatcher.register(e));
  }
}
ListState.watcherSchema = listWatcherSchema;
ListState.stateModel = 'list';
__decorate([LazyGetter(getListProps)], ListState.prototype, "props", void 0);
__decorate([State], ListState.prototype, "listItemsStates", void 0);
__decorate([StateDefine(getListDataSchema(true)), Enumerable(true)], ListState.prototype, "listData", null);
__decorate([State], ListState.prototype, "selectedItems", void 0);
__decorate([StateDefine(pageSchema), State], ListState.prototype, "page", void 0);
__decorate([StateDefine(sizeSchema), State], ListState.prototype, "size", void 0);
__decorate([Getter], ListState.prototype, "filterParams", null);
__decorate([StateDefine(includeSchema), State], ListState.prototype, "include", void 0);
__decorate([StateDefine(excludeSchema), State], ListState.prototype, "exclude", void 0);
__decorate([StateDefine(function () {
  var _filterSchema$items;
  const filterSchema = cloneDeep(bodyFilterField);
  const keyField = (_filterSchema$items = filterSchema.items) === null || _filterSchema$items === void 0 || (_filterSchema$items = _filterSchema$items.fields) === null || _filterSchema$items === void 0 ? void 0 : _filterSchema$items.find(item => item.id === 'k');
  if (keyField) {
    // 写个enum，让他们支持下拉选择
    const enumArray = getEntityEnumArray(this);
    if (enumArray) {
      keyField && (keyField.enum = enumArray);
    }
  }
  return filterSchema;
}), State], ListState.prototype, "filter", void 0);
__decorate([Getter], ListState.prototype, "pureFilter", null);
__decorate([StateDefine(function () {
  var _sortSchema$items;
  const sortSchema = cloneDeep(bodySortField);
  const keySortField = (_sortSchema$items = sortSchema.items) === null || _sortSchema$items === void 0 || (_sortSchema$items = _sortSchema$items.fields) === null || _sortSchema$items === void 0 ? void 0 : _sortSchema$items.find(item => item.id = 'k');
  if (keySortField) {
    // 写个enum，让他们支持下拉选择
    const enumArray = getEntityEnumArray(this);
    if (enumArray) {
      keySortField && (keySortField.enum = enumArray);
    }
  }
  return sortSchema;
}), State], ListState.prototype, "sort", void 0);
__decorate([Getter], ListState.prototype, "pureSort", null);
__decorate([StateDefine(totalSchema), State], ListState.prototype, "total", void 0);
__decorate([StateDefine(function () {
  const {
    collectionData: {
      cgiInfo
    }
  } = this.collection;
  const defaultSchema = {
    id: 'extra',
    type: 'object',
    title: '扩展数据',
    hidden: true
  };
  const info = cgiInfo === null || cgiInfo === void 0 ? void 0 : cgiInfo.list;
  if (!info) return defaultSchema; // 没有配置list接口
  try {
    var _responseSchema$field;
    const responseSchema = getRealResponseSchema(info);
    const extraSchema = cloneDeep(responseSchema === null || responseSchema === void 0 || (_responseSchema$field = responseSchema.fields) === null || _responseSchema$field === void 0 ? void 0 : _responseSchema$field.find(item => item.id === 'extra'));
    if (isObject(extraSchema)) {
      extraSchema.id = defaultSchema.id;
      extraSchema.title = defaultSchema.title;
      return extraSchema;
    }
    return defaultSchema;
  } catch (error) {
    console.error(error);
    return defaultSchema;
  }
}), State], ListState.prototype, "extra", void 0);
__decorate([Getter], ListState.prototype, "supportedQuery", null);
__decorate([Getter], ListState.prototype, "sortParams", null);
__decorate([Getter], ListState.prototype, "orderParams", null);
__decorate([Action(listActions.list, {
  cgiDeps: ['list'],
  schema: {
    id: listActions.list,
    title: '获取列表数据',
    description: '调用**list**接口,获取数据后写入到**listData**',
    actionReturnValue: resSchema
  },
  defineArgs: defineArgs('list')
})], ListState.prototype, "listAction", null);
__decorate([Action(listActions.count, {
  cgiDeps: ['count'],
  schema: {
    id: listActions.count,
    title: '获取总数',
    description: '调用**count**接口,获取数据后写入到**total**',
    actionReturnValue: resSchema
  },
  defineArgs: defineArgs('count')
})], ListState.prototype, "countAction", null);
__decorate([Action(listActions.deleteSelected, {
  cgiDeps: ['delete'],
  schema: {
    id: listActions.saveSelected,
    title: '批量-保存已选中行',
    actionArguments: [isShowMessageSchema],
    actionReturnValue: resSchema
  }
})], ListState.prototype, "deleteSelected", null);
__decorate([Action(listActions.saveSelected, {
  cgiDeps: ['create', 'update'],
  schema: {
    id: listActions.deleteSelected,
    title: '批量-删除已选中行',
    actionArguments: [isShowMessageSchema],
    actionReturnValue: resSchema
  }
})], ListState.prototype, "saveSelected", null);
__decorate([Action(listActions.newItem, {
  schema: {
    id: listActions.newItem,
    title: '新建一行空数据(不提交)',
    actionArguments: [{
      id: 'position',
      type: 'string',
      default: '"tail"',
      required: false,
      title: '插入位置： tail-插入到尾部（默认）；head-插入到头部'
    }]
  }
})], ListState.prototype, "newItem", null);
__decorate([Action(listActions.batchList, {
  schema: {
    id: listActions.batchList,
    title: '批量获取所有数据(不分页)',
    description: '批量获取数据，根据filter过滤,不分页，注意: list接口字段(page/size/filter)必须符合规范',
    actionReturnValue: {
      description: tsCode(`
{
  data: {
    listData: object[] // 返回的列表数据
    total:number       // 总数
    page:number        // 查询了几页
    maxSize:number     // 每页最多个数
  },     
  success: boolean,    //是否调用成功
}     
        `)
    }
  },
  defineArgs: state => {
    const schema = state.getCgiParamSchema('list');
    schema.title = '扩展参数，会和默认参数进行合并';
    schema.required = false;
    return [schema, {
      id: 'maxSize',
      type: 'number',
      required: false,
      title: '每一批请求最大拉取个数,默认10000',
      default: 10000
    }];
  }
})], ListState.prototype, "batchList", null);
//# sourceMappingURL=ListState.js.map
